import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { colors } from '@guryou/colors';
import { EntityTypes } from '@guryou/core';
import { generateWidgetPaymentIntent, getSections, stateIsNotReady, useEffectOnSuccess } from '@guryou/redux';
import { printPrice } from '@guryou/utils';
import { WidgetContext } from './WidgetContext';
import { Icon } from 'components/shared';

const WidgetVideoDetails = ({ video }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { askFirstname, askLastname, askPhone, primaryColor } = useContext(WidgetContext);

  const { activeTab, setActiveServiceId, user, paymentOptions } = useContext(WidgetContext);

  // Use custom primary color if available, otherwise fallback to default mandy color
  const primary = primaryColor || 'var(--feel-primary)';
  const secondary = primaryColor || colors.cabaret;
  const { videos } = useSelector(state => state.widget);
  const sections = useSelector(state => state.sections.all);
  const [open, setOpen] = React.useState(false);
  const [tab, setTab] = React.useState(0);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: user?.firstname || '',
      surname: user?.lastname || '',
      email: user?.email || '',
      phone: user?.phone || '',
    },
  });

  const handleCloseModal = React.useCallback(e => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(false);
    setTab(0);
  }, []);

  const courseOnline = paymentOptions.data.courseOnline;
  const courseOnsite = paymentOptions.data.courseOnline;
  const courseMembership = paymentOptions.data.courseMembership;

  const clickedExpand = isExpanded => {
    setActiveServiceId(isExpanded ? video.id : null);
    if (isExpanded) {
      dispatch(getSections(video.id));
    }
    if (!isExpanded) {
      dispatch(getSections(null));
    }
    setOpen(isExpanded);
  };

  useEffectOnSuccess([state => state.payments.paymentIntent], () => {
    setActiveServiceId(null);
  });

  if (stateIsNotReady(videos) || !sections) {
    return null;
  }

  return (
    <div
      onClick={() => clickedExpand(true)}
      class="group relative flex h-56 flex-col overflow-hidden rounded-2xl bg-white shadow-lg hover:shadow-xl transition-all duration-300 relative overflow-hidden border border-mercury shadow-sm md:h-64 xl:h-72"
      style={{
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
        borderColor: '#e5e7eb',
      }}
      onMouseEnter={e => {
        const color = primary.replace('#', '');
        e.currentTarget.style.boxShadow = `0 20px 25px -5px rgba(${parseInt(color.substring(0, 2), 16)}, ${parseInt(
          color.substring(2, 4),
          16
        )}, ${parseInt(color.substring(4, 6), 16)}, 0.2)`;
        e.currentTarget.style.borderColor = primary + '4D';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1)';
        e.currentTarget.style.borderColor = '#e5e7eb';
      }}
    >
      {/* Image Container */}
      <div class="relative h-3/5 overflow-hidden">
        <img alt="" src={video.imageUrl} loading="lazy" class="absolute inset-0 h-full w-full object-cover object-center" />

        {/* Category Badge */}
        <div class="absolute top-3 left-3">
          <span class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-white/90 text-gray-800 backdrop-blur-sm">
            {video?.categoryName}
          </span>
        </div>

        {/* Price Badge */}
        <div class="absolute top-3 right-3">
          <span
            class="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-semibold text-white shadow-lg"
            style={{ backgroundColor: primary }}
          >
            {printPrice(video.price)}
          </span>
        </div>
      </div>

      {/* Content Container */}
      <div class="flex-1 p-4 bg-white">
        <h2 class="text-lg font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-gray-700 transition-colors duration-200">{video?.name}</h2>

        <div class="flex items-center justify-between mt-auto">
          <button
            type="button"
            style={{ background: `linear-gradient(to right, ${primary}, ${secondary})` }}
            className="text-white w-full flex justify-center items-center font-semibold px-6 py-3 rounded-xl transition-all duration-200 flex items-center shadow-lg hover:shadow-xl"
            onMouseEnter={e => {
              e.currentTarget.style.background = `linear-gradient(to right, ${secondary}, ${primary})`;
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = `linear-gradient(to right, ${primary}, ${secondary})`;
            }}
          >
            <span>{t('HOME_READ_PAGE.readMore')}</span>
          </button>
        </div>
      </div>
      {open && (
        <div
          class="flex overflow-y-auto overflow-x-hidden fixed inset-0 z-50 justify-center items-center w-full h-full bg-black/60 backdrop-blur-sm"
          onClick={e => {
            if (e.target === e.currentTarget) {
              setOpen(false);
              setTab(0);
            }
          }}
        >
          <div class="relative p-4 w-full h-full max-w-4xl max-h-full flex flex-col">
            <div class="relative bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden flex flex-col h-full">
              {/* Header */}
              <div class="relative bg-gradient-to-r from-gray-50 to-white p-6 border-b border-gray-100">
                <div class="flex items-center justify-between">
                  <div class="flex items-center space-x-4">
                    <div class="w-12 h-12 rounded-xl overflow-hidden shadow-lg">
                      <img src={video?.imageUrl} alt="" class="w-full h-full object-cover" />
                    </div>
                    <div>
                      <h3 class="text-xl font-bold text-gray-900">{video?.name}</h3>
                      <p class="text-sm text-gray-500 mt-1">{video?.categoryName}</p>
                    </div>
                  </div>
                  <button
                    onClick={handleCloseModal}
                    type="button"
                    class="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl p-2 transition-all duration-200"
                  >
                    <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span class="sr-only">{t('GENERAL.close')}</span>
                  </button>
                </div>
              </div>
              {tab === 0 && (
                <div className="p-6 flex-1 overflow-y-auto">
                  {/* Video Header */}
                  <div class="mb-6">
                    <div class="flex items-center justify-between mb-4">
                      <div class="flex items-center space-x-3">
                        <span class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-700">
                          {video?.categoryName}
                        </span>
                        <span class="text-2xl font-bold" style={{ color: primary }}>
                          {printPrice(video.price)}
                        </span>
                      </div>
                    </div>
                    <h1 class="text-2xl font-bold text-gray-900 mb-4">{video?.name}</h1>
                  </div>

                  {/* Video Preview */}
                  <div class="mb-6">
                    <div class="relative rounded-xl overflow-hidden shadow-lg bg-gray-100">
                      <img class="w-full h-80 object-cover" src={video?.imageUrl} alt="" />
                      <div class="absolute inset-0 bg-black/20 flex items-center justify-center">
                        <div class="bg-white/90 backdrop-blur-sm rounded-full p-4 shadow-lg">
                          <svg class="w-12 h-12 text-gray-800 ml-1" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  <div class="mb-6">
                    <h3 class="text-lg font-semibold text-gray-900 mb-3">{t('GENERAL.description')}</h3>
                    <div class="text-gray-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: video.description }} />
                  </div>

                  {/* Action Button */}
                  {(courseOnline || courseOnsite || courseMembership) && (
                    <div class="flex justify-end pt-4 border-t border-gray-200">
                      <button
                        onClick={() => setTab(1)}
                        type="button"
                        style={{ backgroundColor: primary }}
                        class="w-full max-w-xs text-white font-semibold py-3 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center space-x-2"
                      >
                        <Icon name="shopping-cart" size="sm" className="mr-2" />
                        <span>{`${t('GENERAL.pay')} ${printPrice(video.price)}`}</span>
                      </button>
                    </div>
                  )}
                </div>
              )}

              {tab === 1 && open && activeTab === EntityTypes.video && (
                <form
                  onSubmit={handleSubmit(data => {
                    dispatch(
                      generateWidgetPaymentIntent({
                        firstname: data.name,
                        lastname: data.surname,
                        phone: data.phone,
                        productId: video.id,
                        email: data.email,
                      })
                    );
                  })}
                >
                  <div class="p-6">
                    {/* Progress Indicator */}
                    {/* Form Body */}
                    <div className="relative">
                      <div className="flex items-center mb-4">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center mr-4 text-white" style={{ backgroundColor: primary }}>
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                          </svg>
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">{t('SERVICES.personalData')}</h3>
                          <p className="text-sm text-gray-600">{t('SERVICES.personalDataStepDescription')}</p>
                        </div>
                      </div>

                      <div className="ml-12">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                              {askFirstname ? `${t('GENERAL.name')} *` : t('GENERAL.name')}
                            </label>
                            <input
                              {...register('name', {
                                required: askFirstname ? t('GENERAL.nameRequired') : false,
                              })}
                              type="text"
                              id="name"
                              style={{
                                borderColor: errors.name ? '#ef4444' : '#d1d5db',
                                '--tw-ring-color': primary,
                              }}
                              className={`w-full h-12 px-4 py-3 border rounded-lg focus:ring-2 transition-colors duration-200 bg-white`}
                              onFocus={e => {
                                e.currentTarget.style.borderColor = primary;
                                e.currentTarget.style.boxShadow = `0 0 0 2px ${primary}40`;
                              }}
                              onBlur={e => {
                                if (!errors.name) {
                                  e.currentTarget.style.borderColor = '#d1d5db';
                                  e.currentTarget.style.boxShadow = 'none';
                                }
                              }}
                              placeholder={t('GENERAL.namePlaceholder')}
                            />
                            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>}
                          </div>

                          <div>
                            <label htmlFor="surname" className="block text-sm font-medium text-gray-700 mb-2">
                              {askLastname ? `${t('GENERAL.surname')} *` : t('GENERAL.surname')}
                            </label>
                            <input
                              {...register('surname', {
                                required: askLastname ? t('GENERAL.surnameRequired') : false,
                              })}
                              type="text"
                              id="surname"
                              style={{
                                borderColor: errors.surname ? '#ef4444' : '#d1d5db',
                              }}
                              className="w-full h-12 px-4 py-3 border rounded-lg focus:ring-2 transition-colors duration-200 bg-white"
                              onFocus={e => {
                                e.currentTarget.style.borderColor = primary;
                                e.currentTarget.style.boxShadow = `0 0 0 2px ${primary}40`;
                              }}
                              onBlur={e => {
                                if (!errors.surname) {
                                  e.currentTarget.style.borderColor = '#d1d5db';
                                  e.currentTarget.style.boxShadow = 'none';
                                }
                              }}
                              placeholder={t('GENERAL.surnamePlaceholder')}
                            />
                            {errors.surname && <p className="mt-1 text-sm text-red-600">{errors.surname.message}</p>}
                          </div>

                          <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                              {t('GENERAL.email')} *
                            </label>
                            <input
                              {...register('email', {
                                required: t('GENERAL.emailRequired'),
                                pattern: {
                                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                  message: t('GENERAL.emailInvalid'),
                                },
                              })}
                              type="email"
                              id="email"
                              style={{
                                borderColor: errors.email ? '#ef4444' : '#d1d5db',
                              }}
                              className="w-full h-12 px-4 py-3 border rounded-lg focus:ring-2 transition-colors duration-200 bg-white"
                              onFocus={e => {
                                e.currentTarget.style.borderColor = primary;
                                e.currentTarget.style.boxShadow = `0 0 0 2px ${primary}40`;
                              }}
                              onBlur={e => {
                                if (!errors.email) {
                                  e.currentTarget.style.borderColor = '#d1d5db';
                                  e.currentTarget.style.boxShadow = 'none';
                                }
                              }}
                              placeholder={t('GENERAL.emailPlaceholder')}
                            />
                            {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>}
                          </div>

                          <div>
                            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                              {askPhone ? `${t('GENERAL.phone')} *` : t('GENERAL.phone')}
                            </label>
                            <input
                              {...register('phone', {
                                required: askPhone ? t('GENERAL.phoneRequired') : false,
                              })}
                              type="tel"
                              id="phone"
                              style={{
                                borderColor: errors.phone ? '#ef4444' : '#d1d5db',
                              }}
                              className="w-full h-12 px-4 py-3 border rounded-lg focus:ring-2 transition-colors duration-200 bg-white"
                              onFocus={e => {
                                e.currentTarget.style.borderColor = primary;
                                e.currentTarget.style.boxShadow = `0 0 0 2px ${primary}40`;
                              }}
                              onBlur={e => {
                                if (!errors.phone) {
                                  e.currentTarget.style.borderColor = '#d1d5db';
                                  e.currentTarget.style.boxShadow = 'none';
                                }
                              }}
                              placeholder={t('GENERAL.phonePlaceholder')}
                            />
                            {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Form Actions */}
                  <div class="bg-gray-50 px-6 py-4 flex justify-end items-center">
                    <button
                      type="submit"
                      style={{ backgroundColor: primary }}
                      class="px-8 py-3 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 flex items-center space-x-2"
                    >
                      <Icon name="shopping-cart" size="sm" className="mr-2" />
                      <span>{`${t('GENERAL.pay')} ${printPrice(video.price)}`}</span>
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WidgetVideoDetails;
